import type { NavigationState } from "@/lib/navigationState.ts";
import { useCallback } from "react";
import { type AroundNavHandler, useLocation } from "wouter";

/**
 * A boundary's decision for a navigation:
 * - `claimed` — an in-place surface took it; do nothing.
 * - `swallow` — intentional no-op (e.g. a max-depth drill); don't navigate or delegate.
 * - `delegate` — hand outward to the parent boundary.
 */
export type ClaimResult = "claimed" | "swallow" | "delegate";

/**
 * Build the `aroundNav` for a nested region boundary. `claim` decides the
 * surface; a `delegate` (or a `fullNavigation` write) is handed to the **parent**
 * router's navigate — not wouter's raw `navigate` arg, which inherits the
 * parent's `hook` and would skip every outer boundary.
 *
 * The returned handler updates when `claim` or the parent navigate function
 * changes. Callers memoize `claim` from the boundary inputs they actually use.
 */
export function useAroundNavBoundary(claim: (to: string) => ClaimResult): AroundNavHandler {
  // useLocation() subscribes to location to reach the parent navigate; required —
  // don't swap for useRouter().
  const [, parentNavigate] = useLocation();

  return useCallback<AroundNavHandler>(
    (_navigate, to, options) => {
      const fullNavigation =
        (options?.state as NavigationState | undefined)?.fullNavigation === true;
      if (fullNavigation) return parentNavigate(to, options);
      switch (claim(to)) {
        case "claimed":
        case "swallow":
          return;
        case "delegate":
          return parentNavigate(to, options);
      }
    },
    [claim, parentNavigate],
  );
}
